import { assign, createMachine, send, sendParent } from 'xstate';
import type { DoneInvokeEvent } from 'xstate';
import { DEFAULT_COST, getCostsContext } from '../utils/events';
import type { PreviewCostsResponse } from '~/subscription-management/api/preview-cost-changes/model';
import { trackCheckout } from '~/subscription-management/utils/tracking';
import type { PricePreviewContext, PricePreviewEvent } from './preview-price.machine.types';

export const PricePreviewMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./preview-price.machine.typegen').Typegen0,
    id: 'preview-price',
    schema: {
      context: {} as PricePreviewContext,
      events: {} as PricePreviewEvent,
      services: {} as {
        getCostPreview: { data: PreviewCostsResponse };
      },
    },
    on: {
      PREVIEW_PRICE: {
        actions: [
          assign<PricePreviewContext, PricePreviewEvent>((_context, pricePreviewContext) => ({
            ...pricePreviewContext,
          })),
        ],
        target: 'shouldGetCostPreview',
      },
    },
    initial: 'shouldGetCostPreview',
    states: {
      shouldGetCostPreview: {
        always: [
          {
            cond: 'isChurning',
            actions: [
              sendParent<PricePreviewContext, PricePreviewEvent>(() => ({
                type: 'UPDATE_PREVIEWED_COSTS',
                costs: getCostsContext(DEFAULT_COST),
                canProceed: true,
                isOnTrialWithBasicAllowances: false,
              })),
              ({ accountId, customerId }) =>
                trackCheckout('Price Preview event', {
                  accountId,
                  customerId,
                  success: false,
                  failureReason: 'churning',
                }),
            ],
            target: 'done',
          },
          {
            target: 'getCostPreview',
          },
        ],
      },
      getCostPreview: {
        invoke: {
          src: 'previewCost',
          id: 'previewCost',
          onDone: [
            {
              actions: [
                sendParent<PricePreviewContext, DoneInvokeEvent<PreviewCostsResponse>>(
                  (context, { data }) => ({
                    type: 'UPDATE_PREVIEWED_COSTS',
                    costs: getCostsContext(context.currentPeriodCostSummary, data),
                    canProceed: true,
                    isOnTrialWithBasicAllowances: false,
                  }),
                ),
                ({ accountId, customerId }) =>
                  trackCheckout('Price Preview event', {
                    accountId,
                    customerId,
                    success: true,
                  }),
              ],
              target: 'done',
            },
          ],
          onError: [
            {
              cond: 'onTrialPreviewingBasicPlans',
              actions: sendParent<PricePreviewContext, DoneInvokeEvent<PreviewCostsResponse>>(
                () => ({
                  type: 'UPDATE_PREVIEWED_COSTS',
                  costs: getCostsContext(DEFAULT_COST),
                  isOnTrialWithBasicAllowances: true,
                  canProceed: false,
                }),
              ),
              target: 'done',
            },
            {
              actions: [
                send<PricePreviewContext, DoneInvokeEvent<PreviewCostsResponse>>('NOTIFICATION'),
                sendParent<PricePreviewContext, DoneInvokeEvent<PreviewCostsResponse>>(
                  (context, { data }) => ({
                    type: 'UPDATE_PREVIEWED_COSTS',
                    costs: getCostsContext(context.currentPeriodCostSummary, data),
                    canProceed: false,
                    isOnTrialWithBasicAllowances: false,
                  }),
                ),
                ({ accountId, customerId }, { data }) =>
                  trackCheckout('Price Preview event', {
                    accountId,
                    customerId,
                    success: false,
                    failureReason: data.message,
                  }),
              ],
              target: 'done',
            },
          ],
        },
      },
      done: {},
    },
  },
  {
    guards: {
      isChurning: (context) => context.isChurning,
      onTrialPreviewingBasicPlans: (context) =>
        context.isOnTrial && context.allowancesToAdd.length === 0,
    },
    services: {
      previewCost: ({
        allowancesToAdd,
        allowancesToRemove,
        billingPeriod,
        customer,
        getCostPreview,
      }: PricePreviewContext) =>
        getCostPreview({
          allowancesToAdd,
          allowancesToRemove,
          billingPeriod,
          customer,
        }),
    },
  },
);
